import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { TeamProvider } from "@/contexts/TeamContext";
import { TagsProvider } from "@/contexts/TagsContext";
import { FamilyProvider } from "@/contexts/FamilyContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { AuthGate } from "@/components/AuthGate";
import { AuthQueryCacheBuster } from "@/components/AuthQueryCacheBuster";
import { toast } from "@/lib/toast";
import { useErrorLogging } from "@/hooks/useErrorLogging";
import { EnvironmentThemeHandler } from "@/components/EnvironmentThemeHandler";
import { TaskEventHandler } from "./components/TaskEventHandler";
import { PushTapRouter } from "./components/PushTapRouter";
import { StockEventHandler } from "./components/StockEventHandler";
import { MealEventHandler } from "./components/MealEventHandler";
import { FailedSyncAutoRetry } from "./components/FailedSyncAutoRetry";
import { TeamThemeHandler } from "./components/TeamThemeHandler";
import { AppLoader } from "./components/AppLoader";
import { DiscoveryProvider } from "@/contexts/DiscoveryContext";
import { BackendStatusProvider } from "@/contexts/BackendStatusContext";
import { AppUpdateManager } from "./components/AppUpdateManager";
import { AppUsageTracker } from "./components/AppUsageTracker";
import { BackgroundPrefetcher } from "./components/BackgroundPrefetcher";
import { ToastSettingsManager } from "./components/ToastSettingsManager";
import { ErrorContextManager } from "./components/ErrorContextManager";
import { NetworkStatusBanner } from "./components/NetworkStatusBanner";
import { SkipToContent, SKIP_TO_CONTENT_TARGET_ID } from "./components/SkipToContent";
import { supabase } from "@/integrations/supabase/client";

const PENDING_OAUTH_CALLBACK_KEY = 'clando-pending-oauth-callback-url';

const extractOAuthTokens = (href: string) => {
  try {
    const url = new URL(href, window.location.origin);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
    const accessToken = hashParams.get('access_token') ?? url.searchParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token') ?? url.searchParams.get('refresh_token');

    return {
      accessToken,
      refreshToken,
      hasAuthToken:
        url.hash.includes('access_token=') ||
        url.hash.includes('refresh_token=') ||
        url.search.includes('access_token=') ||
        url.search.includes('refresh_token='),
    };
  } catch (error) {
    console.warn('[Auth] Failed to parse OAuth callback URL', error);
    return { accessToken: null, refreshToken: null, hasAuthToken: false };
  }
};


// Lazy load pages for better performance
const Index = lazy(() => import("./pages/IndexRouter"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const InstallPage = lazy(() => import("./pages/InstallPage"));
const AdminPageComp = lazy(() => import("./pages/AdminPage"));
const AuthorizeDevicePage = lazy(() => import("./pages/AuthorizeDevicePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Integrations = lazy(() => import("./pages/Integrations"));
const WebhookHandler = lazy(() => import("./pages/WebhookHandler"));
const AppRedirect = lazy(() => import("./pages/AppRedirect"));
const Compare = lazy(() => import("./pages/Compare"));
const ShareTarget = lazy(() => import("./pages/ShareTarget"));
const CompareIndex = lazy(() => import("./pages/CompareIndex"));
const SuperAdminUsersView = lazy(() => import("./components/SuperAdminUsersView").then(m => ({ default: m.SuperAdminUsersView })));
const ProcessCockpitPage = lazy(() => import("./components/cockpit/ProcessCockpitPage"));
const PrivacyPage = lazy(() => import("./pages/Privacy"));

// Lazy load handlers
const TaskActionHandler = lazy(() => import("./components/TaskActionHandler"));
const TaskCreateHandler = lazy(() => import("./components/TaskCreateHandler"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep previous data visible while refetching — prevents flashing
      refetchOnWindowFocus: false,
      // Do not refetch on mount when data is still within staleTime.
      // Combined with longer staleTime + localStorage caches, this prevents
      // request bursts when navigating between routes/tabs.
      refetchOnMount: false,
      // Don't refetch on reconnect by default — visibility handlers in our
      // hooks already cover the "tab returned" case with throttling.
      refetchOnReconnect: false,
      // Default stale time: 10 minutes. Hot data (tasks, stock) overrides
      // this with shorter values. Cold data (tags, teams, states, locations)
      // can stay stale much longer.
      staleTime: 10 * 60 * 1000,
      // Keep cached data in memory for 30 minutes after last use.
      gcTime: 30 * 60 * 1000,
    },
  },
});

const App = () => {
  // Initialize error logging (captures global errors and syncs to backend)
  useErrorLogging();

  // Listen for app update event
  useEffect(() => {
    const handleAppUpdated = () => {
      // Don't show the "App bijgewerkt" toast on native platforms
      // as it's confusing for users who expect updates via the App Store
      if ((window as any).Capacitor?.isNativePlatform()) return;

      toast.success("App bijgewerkt! 🎉", {
        channel: 'general',
        description: "De nieuwste versie is geïnstalleerd.",
        duration: 4000,
      });
    };

    window.addEventListener('app-updated', handleAppUpdated);
    return () => window.removeEventListener('app-updated', handleAppUpdated);
  }, []);

  // Handle Deep Links (for OAuth redirects on mobile)
  useEffect(() => {
    const handleAppUrlOpen = async (event: { url: string }) => {
      if (!event.url) return;

      console.log('[DeepLink] Processing URL:', event.url);

      try {
        const url = new URL(event.url);
        console.log('[DeepLink] Host:', url.host, 'Path:', url.pathname);

        // Handle Supabase auth callback
        // Check host (for app.clando:// scheme) and pathname (for https:// scheme)
        const isLoginCallback =
          url.host === 'login-callback' ||
          url.pathname.includes('login-callback') ||
          url.hash.includes('access_token');

        if (isLoginCallback) {
          console.log('[DeepLink] Detected login callback, extracting tokens...');
          // Tokens can be in hash (typical for OAuth) or search params
          const hash = url.hash.substring(1);
          const hashParams = new URLSearchParams(hash);

          let accessToken = hashParams.get('access_token');
          let refreshToken = hashParams.get('refresh_token');

          // Fallback to query parameters
          if (!accessToken) accessToken = url.searchParams.get('access_token');
          if (!refreshToken) refreshToken = url.searchParams.get('refresh_token');

          if (accessToken && refreshToken) {
            console.log('[DeepLink] Tokens found, setting session in Supabase...');
            toast.info("Inloggen verwerken...", { id: 'deep-link-loading' });

            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (error) {
              console.error('[DeepLink] setSession error:', error.message);
              toast.error('Inloggen mislukt via deep link: ' + error.message);
            } else {
              console.log('[DeepLink] Session successfully established');
              toast.success('Succesvol ingelogd!', { id: 'deep-link-loading' });
            }
          } else {
            console.warn('[DeepLink] Callback detected but tokens were missing in URL components');
            console.log('[DeepLink] Hash length:', url.hash.length, 'Search length:', url.search.length);
          }
        }
      } catch (err) {
        console.error('[DeepLink] Error parsing URL:', err);
      }
    };

    // Listen for the appUrlOpen event from Capacitor App plugin
    const setupDeepLinkListener = async () => {
      try {
        const { App: CapApp } = await import('@capacitor/app');

        // Listen for new URL events while app is running
        CapApp.addListener('appUrlOpen', (event) => {
          console.log('[DeepLink] App received URL while running');
          handleAppUrlOpen(event);
        });

        // Handle the URL that launched the app (critical for cold starts)
        const launchUrl = await CapApp.getLaunchUrl();
        if (launchUrl && launchUrl.url) {
          console.log('[DeepLink] App launched with URL (cold start):', launchUrl.url);
          // Small delay to ensure the app is fully hydrated before processing auth
          setTimeout(() => handleAppUrlOpen(launchUrl), 500);
        }
      } catch (e) {
        console.warn('[DeepLink] Could not initialize App plugin', e);
      }
    };

    if ((window as any).Capacitor?.isNativePlatform()) {
      setupDeepLinkListener();
    }
  }, []);

  // Explicitly persist OAuth tokens as soon as React starts. This is a second
  // safety layer for the published PWA: returning users can still be controlled
  // by an older service worker or hit an early update reload while the callback
  // URL contains tokens. The inline script in index.html stores that original
  // URL before any reload; this effect recovers it and sets the session directly.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const pendingHref = (() => {
      try {
        return sessionStorage.getItem(PENDING_OAUTH_CALLBACK_KEY);
      } catch {
        return null;
      }
    })();

    const candidates = [window.location.href, pendingHref].filter(Boolean) as string[];
    const tokenSource = candidates.map(extractOAuthTokens).find((tokens) => tokens.accessToken && tokens.refreshToken);
    const sawAuthCallback = candidates.some((href) => extractOAuthTokens(href).hasAuthToken);
    if (!sawAuthCallback) return;

    let cancelled = false;
    const cleanup = () => {
      try {
        sessionStorage.removeItem(PENDING_OAUTH_CALLBACK_KEY);
      } catch {
        // Ignore storage access issues.
      }
      if (extractOAuthTokens(window.location.href).hasAuthToken) {
        try {
          window.history.replaceState({}, '', window.location.pathname);
        } catch (error) {
          console.warn('[Auth] Failed to clean OAuth callback URL', error);
        }
      }
    };

    if (!tokenSource?.accessToken || !tokenSource?.refreshToken) {
      cleanup();
      return;
    }

    supabase.auth
      .setSession({
        access_token: tokenSource.accessToken,
        refresh_token: tokenSource.refreshToken,
      })
      .then(({ error }) => {
        if (cancelled) return;
        if (error) {
          console.error('[Auth] Failed to persist OAuth callback session:', error.message);
        } else {
          console.info('[Auth] OAuth callback session persisted');
        }
      })
      .finally(() => {
        if (!cancelled) cleanup();
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // [SEC-M5] Strip OAuth tokens from the URL once Supabase has consumed them on
  // web. Tokens in window.location.hash leak via browser history, the Referer
  // header, and third-party scripts. IMPORTANT: Supabase's `detectSessionInUrl`
  // is ASYNC (it runs during the client's `_initialize`, not synchronously) —
  // stripping the hash on mount races ahead of it and silently drops the
  // session, which breaks every fresh Google/OAuth login while existing
  // refresh-token sessions keep working (so it goes unnoticed for weeks). We
  // therefore wait for Supabase's first auth-state event (emitted AFTER URL
  // detection completes) before clearing the hash, with a safety-net timeout.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasToken =
      window.location.hash.includes('access_token=') ||
      window.location.hash.includes('refresh_token=') ||
      window.location.search.includes('access_token=') ||
      window.location.search.includes('refresh_token=');
    if (!hasToken) return;

    let done = false;
    const strip = () => {
      if (done) return;
      done = true;
      try {
        window.history.replaceState({}, '', window.location.pathname);
      } catch (e) {
        console.warn('[Auth] Failed to strip tokens from URL', e);
      }
      sub?.data.subscription.unsubscribe();
      clearTimeout(timer);
    };

    // First auth-state event fires after detectSessionInUrl has run.
    const sub = supabase?.auth.onAuthStateChange(() => strip());
    // Safety net: clear the hash even if no event arrives (e.g. invalid tokens).
    const timer = setTimeout(strip, 2500);
    return () => {
      sub?.data.subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <SettingsProvider>
        <BackendStatusProvider>
        <EnvironmentThemeHandler />
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <AuthProvider>
              <AppUsageTracker />
              <AuthQueryCacheBuster />
              <TeamProvider>
                <ErrorContextManager />
                <TeamThemeHandler />
                <TagsProvider>
                  <FamilyProvider>
                    <DiscoveryProvider>
                      <Toaster />
                      <Sonner />
                      <ToastSettingsManager />
                      <AppUpdateManager />
                      <TaskEventHandler />
                      <StockEventHandler />
                      <MealEventHandler />
                      <FailedSyncAutoRetry />
                      <NetworkStatusBanner />
                      <BackgroundPrefetcher />

                      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <PushTapRouter />
                        <SkipToContent />
                        <div id={SKIP_TO_CONTENT_TARGET_ID} tabIndex={-1} className="outline-none">
                        <Suspense fallback={null}>
                          <Routes>
                            {/* Protected routes - require auth in production */}
                            <Route path="/" element={<AuthGate><Index /></AuthGate>} />
                            <Route path="/admin" element={<AuthGate><AdminPageComp /></AuthGate>} />
                            <Route path="/:teamSlug/cockpit" element={<AuthGate><ProcessCockpitPage /></AuthGate>} />
                            <Route path="/:teamSlug" element={<AuthGate><TeamPage /></AuthGate>} />

                            {/* Public routes - always accessible */}
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route path="/install" element={<InstallPage />} />
                            <Route path="/vs" element={<CompareIndex />} />
                            <Route path="/vs/:competitor" element={<Compare />} />
                            <Route path="/app-redirect" element={<AppRedirect />} />
                            <Route path="/share" element={<AuthGate><ShareTarget /></AuthGate>} />
                            <Route path="/authorize-device" element={<AuthorizeDevicePage />} />
                            <Route path="/webhook/:slug" element={<WebhookHandler />} />
                            <Route path="/admin/users" element={<AuthGate><SuperAdminUsersView /></AuthGate>} />

                            {/* API/Action Routes - require auth */}
                            <Route path="/api/tasks/:taskId/:action" element={<AuthGate><TaskActionHandler /></AuthGate>} />
                            <Route path="/api/create-task" element={<AuthGate><TaskCreateHandler /></AuthGate>} />

                            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                        </div>
                      </BrowserRouter>
                    </DiscoveryProvider>
                  </FamilyProvider>
                </TagsProvider>
              </TeamProvider>
            </AuthProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </BackendStatusProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
};

export default App;
